/**
 * Orders items by the `sort` URL parameter of a `SortableTh` table: a bare column key ascends and a
 * `-` prefix descends. Items that tie on the column are ordered by `tiebreak`, which always
 * ascends. Without a recognized column key, items are ordered ascending by `fallback`.
 *
 * @param items - Items to order; the array is not mutated.
 * @param sortParam - The table's `sort` URL parameter.
 * @param comparators - Ascending comparator for each sortable column key.
 * @param orderRules - `fallback` orders items when no column is recognized; `tiebreak` orders
 * items that tie on the sorted column.
 * @returns A new array with the items in display order.
 */
export function sortBySortParam<Item, SortKey extends string>(
  items: Item[],
  sortParam: string | null,
  comparators: Record<SortKey, Compare<Item>>,
  orderRules: { fallback: Compare<Item>; tiebreak: Compare<Item> },
): Item[] {
  const sortKey = sortParam?.replace(/^-/, "");
  const isSortKey = (key: string | undefined): key is SortKey =>
    key !== undefined && Object.hasOwn(comparators, key);

  const compareByColumn = isSortKey(sortKey) ? comparators[sortKey] : orderRules.fallback;
  const direction = isSortKey(sortKey) && sortParam?.startsWith("-") ? -1 : 1;

  return items.toSorted((a, b) => direction * compareByColumn(a, b) || orderRules.tiebreak(a, b));
}

type Compare<Item> = (a: Item, b: Item) => number;
